import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
var _excluded = ["id", "date"];
import { createSlice } from '@reduxjs/toolkit';
import { LoadingStatus, generateFileThunk, getHistoryThunk } from "../../../../shared/model";
import { SessionStatus, getMockOperatorInitialMessage, getNewSession } from "../../../../shared/api";
import { deepMerge, getLastSession, getTodayISODate, saveCaptchaStageHistory } from "../../../../shared/lib";
import { addMessagesToLastSession, checkAndUpdateMessageStatus, checkLastSession, createLocalHistory, quickSearchSession, sortSessionMessages } from "./utils";
var initialState = {
  history: {
    count: 0,
    list: []
  },
  resubmitMessages: [],
  imgStatus: LoadingStatus.idle,
  status: LoadingStatus.idle,
  unreadedMessages: [],
  resendMessageId: null,
  isNoNAuthCaptchaStage: false,
  imgPreview: {
    isImgPreviewOpen: false,
    type: null
  }
};
export var messageSlice = createSlice({
  name: 'message',
  initialState: initialState,
  reducers: {
    addMessage: function addMessage(state, action) {
      var _getLastSession;
      checkLastSession(state);
      addMessagesToLastSession(state, action.payload);
      saveCaptchaStageHistory((_getLastSession = getLastSession(state.history)) === null || _getLastSession === void 0 ? void 0 : _getLastSession.messages, state.isNoNAuthCaptchaStage);
    },
    addMessages: function addMessages(state, action) {
      var _getLastSession2;
      checkLastSession(state);
      addMessagesToLastSession.apply(void 0, [state].concat(_toConsumableArray(action.payload)));
      saveCaptchaStageHistory((_getLastSession2 = getLastSession(state.history)) === null || _getLastSession2 === void 0 ? void 0 : _getLastSession2.messages, state.isNoNAuthCaptchaStage);
    },
    updateMessageStatus: function updateMessageStatus(state, action) {
      var currentSession = getLastSession(state.history);
      var message = currentSession === null || currentSession === void 0 ? void 0 : currentSession.messages.find(function (_ref) {
        var messageId = _ref.messageId;
        return messageId === action.payload.id;
      });
      if (message) {
        message.messageStatus = action.payload.status;
      }
      saveCaptchaStageHistory(currentSession === null || currentSession === void 0 ? void 0 : currentSession.messages, state.isNoNAuthCaptchaStage);
    },
    updateMessage: function updateMessage(state, action) {
      var _action$payload = action.payload,
        id = _action$payload.id,
        date = _action$payload.date,
        payload = _objectWithoutProperties(_action$payload, _excluded);
      var _quickSearchSession = quickSearchSession(date, state.history.list),
        _quickSearchSession2 = _slicedToArray(_quickSearchSession, 1),
        currentSession = _quickSearchSession2[0];
      var message = currentSession === null || currentSession === void 0 ? void 0 : currentSession.messages.find(function (_ref2) {
        var messageId = _ref2.messageId;
        return messageId === id;
      });
      if (message) {
        Object.assign(message, deepMerge(message, payload));
        if (date) {
          sortSessionMessages(state);
        }
      }
      saveCaptchaStageHistory(currentSession === null || currentSession === void 0 ? void 0 : currentSession.messages, state.isNoNAuthCaptchaStage);
    },
    deleteMessage: function deleteMessage(state, action) {
      var _quickSearchSession3 = quickSearchSession(action.payload.date, state.history.list),
        _quickSearchSession4 = _slicedToArray(_quickSearchSession3, 1),
        currentSession = _quickSearchSession4[0];
      if (currentSession) {
        currentSession.messages = currentSession.messages.filter(function (_ref3) {
          var messageId = _ref3.messageId;
          return messageId !== action.payload.id;
        });
      }
      saveCaptchaStageHistory(currentSession === null || currentSession === void 0 ? void 0 : currentSession.messages, state.isNoNAuthCaptchaStage);
    },
    deleteMessageFromPreview: function deleteMessageFromPreview(state, action) {
      var currentSession = state.history.list.slice(-1)[0];
      if (currentSession) {
        currentSession.messages = currentSession.messages.filter(function (_ref4) {
          var messageId = _ref4.messageId;
          return messageId !== action.payload;
        });
      }
      state.imgPreview = {
        isImgPreviewOpen: false,
        type: null
      };
      saveCaptchaStageHistory(currentSession === null || currentSession === void 0 ? void 0 : currentSession.messages, state.isNoNAuthCaptchaStage);
    },
    setReplyMessage: function setReplyMessage(state, action) {
      if (!action.payload) {
        state.replyMessage = undefined;
        return;
      }
      for (var i = 0; i < state.history.list.length; i += 1) {
        var messages = state.history.list[i].messages;
        for (var j = 0; j < messages.length; j += 1) {
          var message = messages[j];
          if (message.messageId === action.payload) {
            state.replyMessage = message;
            return;
          }
        }
      }
      state.replyMessage = undefined;
    },
    checkIfMessageSent: function checkIfMessageSent(state, _ref5) {
      var payload = _ref5.payload;
      var date = payload.date,
        messageId = payload.messageId;
      var _quickSearchSession5 = quickSearchSession(date, state.history.list),
        _quickSearchSession6 = _slicedToArray(_quickSearchSession5, 2),
        currentSession = _quickSearchSession6[0],
        index = _quickSearchSession6[1];
      var removeFromResubmit = function removeFromResubmit() {
        state.resubmitMessages = state.resubmitMessages.filter(function (el) {
          return el.messageId !== messageId;
        });
      };
      if (checkAndUpdateMessageStatus(messageId, currentSession)) {
        saveCaptchaStageHistory(currentSession === null || currentSession === void 0 ? void 0 : currentSession.messages, state.isNoNAuthCaptchaStage);
        removeFromResubmit();
        return;
      }
      var prevSession = state.history.list[index - 1];
      if (prevSession && checkAndUpdateMessageStatus(messageId, prevSession)) {
        removeFromResubmit();
        return;
      }
      var nextSession = state.history.list[index + 1];
      var flag = checkAndUpdateMessageStatus(messageId, nextSession);
      if (flag) {
        removeFromResubmit();
      } else {
        state.resubmitMessages.push(payload);
      }
    },
    setResendMessageId: function setResendMessageId(state, action) {
      state.resendMessageId = action.payload;
    },
    startSession: function startSession(state, action) {
      state.history.list.push(action.payload);
    },
    updateSession: function updateSession(state, action) {
      var lastSession = getLastSession(state.history);
      if (lastSession && (lastSession === null || lastSession === void 0 ? void 0 : lastSession.status) !== SessionStatus.Finished) {
        var sessionData = action.payload.data;
        var now = getTodayISODate();
        lastSession.id = sessionData.sessionId;
        lastSession.status = SessionStatus.Started;
        lastSession.updatedAt = now;
      }
    },
    updateSessionList: function updateSessionList(state, action) {
      state.history = {
        list: state.history.list.concat(action.payload),
        count: state.history.count + action.payload.length
      };
    },
    clearErrors: function clearErrors(state) {
      state.errors = [];
    },
    // Reducers for unreaded messages
    removeUnreadedMessage: function removeUnreadedMessage(state, action) {
      state.unreadedMessages = state.unreadedMessages.filter(function (el) {
        return el.messageId !== action.payload;
      });
    },
    addUnreadedMessage: function addUnreadedMessage(state, action) {
      state.unreadedMessages.push(action.payload);
    },
    clearUnreadedMessages: function clearUnreadedMessages(state) {
      state.unreadedMessages = [];
    },
    setIsNoNAuthCaptchaStage: function setIsNoNAuthCaptchaStage(state, action) {
      state.isNoNAuthCaptchaStage = action.payload;
    },
    setContext: function setContext(state, action) {
      state.context = action.payload;
    },
    setStoreImgPreview: function setStoreImgPreview(state, action) {
      state.imgPreview = action.payload;
    },
    setStatus: function setStatus(state, action) {
      state.status = action.payload;
    },
    setWelcomeMessage: function setWelcomeMessage(state, action) {
      state.welcomeMessage = action.payload;
    }
  },
  extraReducers: function extraReducers(builder) {
    builder.addCase(getHistoryThunk.fulfilled, function (state, action) {
      var history = createLocalHistory(action.payload);
      var lastSession = getLastSession(history);

      // Добавляем приветственное моковое сообщение от оператора если сессия завершена
      if ((!lastSession || lastSession.status === SessionStatus.Finished) && state.welcomeMessage) {
        if (!lastSession) {
          // Если истории нет создаем дохлую историю специально для стартового сообщения
          history.list.push(getNewSession(SessionStatus.Finished));
        }

        // Новая сессия / предыдущая
        var currentLastSession = getLastSession(history);
        var initialMessage = getMockOperatorInitialMessage(state.welcomeMessage);
        currentLastSession.messages.push(initialMessage);
        currentLastSession.updatedAt = initialMessage.date;
      }
      state.history = history;
      state.status = LoadingStatus.fulfilled;
    }).addCase(getHistoryThunk.rejected, function (state) {
      state.status = LoadingStatus.failed;
    }).addCase(generateFileThunk.pending, function (state) {
      state.imgStatus = LoadingStatus.loading;
    }).addCase(generateFileThunk.fulfilled, function (state) {
      state.imgStatus = LoadingStatus.fulfilled;
    }).addCase(generateFileThunk.rejected, function (state) {
      state.imgStatus = LoadingStatus.failed;
    });
  }
});
var _messageSlice$actions = messageSlice.actions,
  addMessage = _messageSlice$actions.addMessage,
  addMessages = _messageSlice$actions.addMessages,
  updateMessage = _messageSlice$actions.updateMessage,
  updateMessageStatus = _messageSlice$actions.updateMessageStatus,
  deleteMessage = _messageSlice$actions.deleteMessage,
  setReplyMessage = _messageSlice$actions.setReplyMessage,
  setContext = _messageSlice$actions.setContext,
  setStoreImgPreview = _messageSlice$actions.setStoreImgPreview,
  checkIfMessageSent = _messageSlice$actions.checkIfMessageSent,
  setResendMessageId = _messageSlice$actions.setResendMessageId,
  setWelcomeMessage = _messageSlice$actions.setWelcomeMessage,
  startSession = _messageSlice$actions.startSession,
  updateSession = _messageSlice$actions.updateSession,
  updateSessionList = _messageSlice$actions.updateSessionList,
  clearErrors = _messageSlice$actions.clearErrors,
  setStatus = _messageSlice$actions.setStatus,
  removeUnreadedMessage = _messageSlice$actions.removeUnreadedMessage,
  deleteMessageFromPreview = _messageSlice$actions.deleteMessageFromPreview,
  addUnreadedMessage = _messageSlice$actions.addUnreadedMessage,
  clearUnreadedMessages = _messageSlice$actions.clearUnreadedMessages,
  setIsNoNAuthCaptchaStage = _messageSlice$actions.setIsNoNAuthCaptchaStage;
export { addMessage, addMessages, updateMessage, updateMessageStatus, deleteMessage, setReplyMessage, setContext, setStoreImgPreview, checkIfMessageSent, setResendMessageId, setWelcomeMessage, startSession, updateSession, updateSessionList, clearErrors, setStatus, removeUnreadedMessage, deleteMessageFromPreview, addUnreadedMessage, clearUnreadedMessages, setIsNoNAuthCaptchaStage };